import React, { createContext, useContext, useState, useEffect } from "react";

export type Language = "zh" | "en" | "ja";

interface LanguageContextType {
  currentLanguage: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isTransitioning?: boolean;
  availableLanguages?: Array<{ code: Language; name: string; nativeName: string }>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// 多语言翻译资源
const translations: Record<Language, Record<string, string>> = {
  zh: {
    "quiz.title": "找到最适合您的产品",
    "quiz.subtitle": "通过 7 个简单问题，我们将为您推荐最适合的电子烟产品",
    "quiz.question": "问题",
    "quiz.progress": "进度",
    "quiz.analyzing": "正在为您匹配最佳产品",
    "quiz.analyzing.subtitle": "分析您的需求中，请稍候...",
    "quiz.analyzing.step1": "收集您的偏好数据",
    "quiz.analyzing.step2": "计算产品匹配度",
    "quiz.analyzing.step3": "生成个性化推荐",
    "result.title": "推荐结果",
    "result.subtitle": "基于您的答题，我们为您推荐：",
    "result.matchPercentage": "匹配度",
    "result.whyRecommend": "为什么推荐这个？",
    "result.feedback": "这个推荐有帮助吗？",
    "result.helpful": "有帮助",
    "result.notHelpful": "没帮助",
    "result.buy": "立即购买",
    "result.download": "下载海报",
    "result.share": "分享",
    "result.retake": "重新测试",
    "result.history": "历史",
    "history.title": "推荐历史",
    "history.empty": "暂无推荐历史",
    "history.compare": "对比视图",
    "history.back": "返回",
    "history.rename": "重命名",
    "history.favorite": "收藏",
    "history.delete": "删除",
    "history.matchPercentage": "匹配度",
    "history.helpful": "用户认为有帮助",
    "history.notHelpful": "用户认为没帮助",
    "share.title": "分享推荐结果",
    "share.link": "分享链接",
    "share.copied": "链接已复制！",
    "share.close": "关闭",
  },
  en: {
    "quiz.title": "Find Your Perfect Product",
    "quiz.subtitle": "Through 7 simple questions, we'll recommend the best vape product for you",
    "quiz.question": "Question",
    "quiz.progress": "Progress",
    "quiz.analyzing": "Matching You with the Best Product",
    "quiz.analyzing.subtitle": "Analyzing your preferences, please wait...",
    "quiz.analyzing.step1": "Collecting your preferences",
    "quiz.analyzing.step2": "Calculating product match",
    "quiz.analyzing.step3": "Generating personalized recommendation",
    "result.title": "Recommendation Result",
    "result.subtitle": "Based on your answers, we recommend:",
    "result.matchPercentage": "Match Rate",
    "result.whyRecommend": "Why this recommendation?",
    "result.feedback": "Was this recommendation helpful?",
    "result.helpful": "Helpful",
    "result.notHelpful": "Not Helpful",
    "result.buy": "Buy Now",
    "result.download": "Download Poster",
    "result.share": "Share",
    "result.retake": "Retake Quiz",
    "result.history": "History",
    "history.title": "Recommendation History",
    "history.empty": "No recommendation history",
    "history.compare": "Compare View",
    "history.back": "Back",
    "history.rename": "Rename",
    "history.favorite": "Favorite",
    "history.delete": "Delete",
    "history.matchPercentage": "Match Rate",
    "history.helpful": "User found this helpful",
    "history.notHelpful": "User found this not helpful",
    "share.title": "Share Recommendation",
    "share.link": "Share Link",
    "share.copied": "Link copied!",
    "share.close": "Close",
  },
  ja: {
    "quiz.title": "あなたに最適な製品を見つけましょう",
    "quiz.subtitle": "7つの簡単な質問を通じて、あなたに最適な電子タバコ製品をお勧めします",
    "quiz.question": "質問",
    "quiz.progress": "進捗",
    "quiz.analyzing": "あなたに最適な製品をマッチングしています",
    "quiz.analyzing.subtitle": "あなたの好みを分析しています。お待ちください...",
    "quiz.analyzing.step1": "あなたの好みを収集しています",
    "quiz.analyzing.step2": "製品のマッチ度を計算しています",
    "quiz.analyzing.step3": "パーソナライズされた推奨を生成しています",
    "result.title": "推奨結果",
    "result.subtitle": "あなたの回答に基づいて、以下をお勧めします：",
    "result.matchPercentage": "マッチ率",
    "result.whyRecommend": "なぜこの推奨ですか？",
    "result.feedback": "この推奨は役に立ちましたか？",
    "result.helpful": "役に立った",
    "result.notHelpful": "役に立たなかった",
    "result.buy": "今すぐ購入",
    "result.download": "ポスターをダウンロード",
    "result.share": "共有",
    "result.retake": "クイズをやり直す",
    "result.history": "履歴",
    "history.title": "推奨履歴",
    "history.empty": "推奨履歴がありません",
    "history.compare": "比較ビュー",
    "history.back": "戻る",
    "history.rename": "名前変更",
    "history.favorite": "お気に入り",
    "history.delete": "削除",
    "history.matchPercentage": "マッチ率",
    "history.helpful": "ユーザーが役に立つと判断しました",
    "history.notHelpful": "ユーザーが役に立たないと判断しました",
    "share.title": "推奨を共有",
    "share.link": "共有リンク",
    "share.copied": "リンクがコピーされました！",
    "share.close": "閉じる",
  },
};

const AVAILABLE_LANGUAGES: Array<{ code: Language; name: string; nativeName: string }> = [
  { code: "zh", name: "Chinese", nativeName: "中文" },
  { code: "en", name: "English", nativeName: "English" },
  { code: "ja", name: "Japanese", nativeName: "日本語" },
];

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [currentLanguage, setLanguageState] = useState<Language>(() => {
    // 从 localStorage 读取语言设置，默认为中文
    const stored = localStorage.getItem("language");
    return (stored as Language) || "zh";
  });

  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    localStorage.setItem("language", currentLanguage);
    // 设置 HTML 的 lang 属性
    if (typeof document !== "undefined") {
      document.documentElement.lang = currentLanguage;
    }
  }, [currentLanguage]);

  const setLanguage = (lang: Language) => {
    if (lang === currentLanguage) return;
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setLanguageState(lang);
      setIsTransitioning(false);
    }, 300);
    return () => clearTimeout(timer);
  };

  const t = (key: string): string => {
    return translations[currentLanguage][key] || translations.zh[key] || key;
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        setLanguage,
        t,
        isTransitioning,
        availableLanguages: AVAILABLE_LANGUAGES,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}
